import {
  ArrowLeft,
  Bot,
  BriefcaseBusiness,
  LoaderCircle,
  Mail,
  MessageSquareText,
  Play,
  UserRound,
} from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { Tabs } from "../../components/ui/Tabs";
import { ErrorState, LoadingState, Skeleton } from "../../components/ui/Feedback";
import { StatusBadge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import CustomerOverview from "./CustomerOverview";
import EmailContent from "./EmailContent";
import CustomerContext from "./CustomerContext";
import AiResearchPanel from "./AiResearchPanel";
import ReplyComposer from "./ReplyComposer";

const TABS = [
  { value: "mail", label: "邮件与客户", icon: Mail },
  { value: "analysis", label: "AI 分析", icon: Bot },
  { value: "reply", label: "回复与发送", icon: MessageSquareText },
  { value: "context", label: "历史与跟进", icon: BriefcaseBusiness },
];

const PROGRESS_LABELS = {
  connecting: "正在连接实时处理进度",
  processing_started: "AI 处理已开始",
  translation_started: "正在生成中文翻译",
  translation_completed: "中文翻译已完成",
  research_started: "正在准备客户背调",
  search_started: "正在检索公开信息",
  search_results_ready: "公开检索完成，正在筛选来源",
  source_fetching: "正在并行读取公开来源",
  source_fetch_completed: "公开来源读取完成",
  search_completed: "检索证据已整理，正在生成背调",
  reply_started: "正在生成英文回复草稿",
  reply_completed: "英文回复草稿已完成",
  content_delta: "AI 正在生成内容",
  completed: "AI 处理已完成",
  failed: "AI 处理失败",
};

export default function InquiryCenterPane({
  inquiry, context, loading, error, onRetry, onOriginal, onRating, onOpenRating, onOpenHistory,
  researchDetail, secondaryLoading, secondaryError,
  onSaveFollowUp, onAddNote, contextBusy, onMobileBack,
  onTranslate, translating,
  bodyLoading, bodyError, onBodyRetry,
  activeTab, onTab, settings, suggestions, busy, onResearch, onOpenTemplates,
  onGenerate, onSaveDraft, onRecipientsSave, onOpenSend, onPreview, onAutoProcess,
  aiProgress,
}) {
  const [internalTab, setInternalTab] = useState("mail");
  const centerScrollRef = useRef(null);
  const tab = activeTab || internalTab;
  const setTab = onTab || setInternalTab;

  useLayoutEffect(() => {
    if (centerScrollRef.current) centerScrollRef.current.scrollTop = 0;
  }, [inquiry?.id, tab]);

  return (
    <section className="inquiry-center-pane">
      <header className="detail-title">
        <button type="button" className="mobile-detail-button" onClick={onMobileBack} aria-label="返回询盘列表"><ArrowLeft size={18} /></button>
        <div className="detail-title__identity">
          <span className="avatar"><UserRound size={17} /></span>
          <span><h2>{inquiry?.customer_name || inquiry?.sender_name || "选择一封询盘"}</h2><small className="truncate">{inquiry?.subject || "查看客户邮件与处理上下文"}</small></span>
        </div>
        {inquiry ? (
          <div className="detail-title__actions">
            <StatusBadge status={inquiry.status} label={inquiry.status_label} />
            {inquiry.status === "new" || inquiry.status === "failed" ? (
              <Button size="sm" variant="ghost" icon={Play} loading={busy === "queue-ai"} onClick={onAutoProcess}>一键处理</Button>
            ) : null}
          </div>
        ) : null}
      </header>
      {inquiry ? <Tabs items={TABS} value={tab} onChange={setTab} /> : null}
      <div className="ai-live-progress-slot">
        {aiProgress && !["completed"].includes(aiProgress.event) ? (
          <div className={`ai-live-progress ${aiProgress.event === "failed" ? "is-error" : ""}`} role="status" aria-live="polite">
            <div className="ai-live-progress__status">
              {aiProgress.event !== "failed" ? <LoaderCircle size={16} className="spin" /> : <Bot size={16} />}
              <strong>{PROGRESS_LABELS[aiProgress.event] || "AI 正在处理"}</strong>
              <span>{Math.round(Number(aiProgress.elapsed_seconds) || 0)} 秒</span>
            </div>
            {aiProgress.event === "failed" ? (
              <p>{aiProgress.message || "本次处理未完成，请稍后重试。"}</p>
            ) : null}
            {aiProgress.event === "content_delta" && aiProgress.channel ? (
              <p className="ai-live-progress__preview">
                {(aiProgress.partial?.[aiProgress.channel] || "").slice(-320)}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      <div ref={centerScrollRef} className={`center-scroll ${tab === "analysis" || tab === "reply" ? "is-ai-workspace" : ""}`}>
        {loading && inquiry?._summary && tab === "mail" ? (
          <>
            <CustomerOverview inquiry={inquiry} onRating={onRating} onOpenRating={onOpenRating} />
            <section className="email-content inquiry-detail-skeleton" aria-label="邮件正文加载中">
              <header className="content-section-header"><div><h3>客户邮件</h3><small>正在读取正文与翻译</small></div></header>
              <article className="email-paper"><Skeleton rows={6} /></article>
            </section>
          </>
        ) : loading && inquiry?._summary ? <Skeleton rows={8} /> : loading && !inquiry ? <LoadingState label="正在加载询盘详情…" /> : error && !inquiry ? <ErrorState message={error} onRetry={onRetry} /> : !inquiry ? (
          <div className="select-inquiry-placeholder"><Mail size={32} /><strong>从左侧选择一封询盘</strong><p>客户邮件、历史往来和 AI 处理结果将在这里展开。</p></div>
        ) : tab === "mail" ? (
          <>
            <CustomerOverview inquiry={inquiry} onRating={onRating} onOpenRating={onOpenRating} />
            <EmailContent
              inquiry={inquiry}
              onOriginal={onOriginal}
              onTranslate={onTranslate}
              translating={translating}
              bodyLoading={bodyLoading}
              bodyError={bodyError}
              onBodyRetry={onBodyRetry}
            />
          </>
        ) : tab === "analysis" ? (
          <AiResearchPanel
            inquiry={inquiry}
            researchDetail={researchDetail}
            secondaryLoading={secondaryLoading}
            secondaryError={secondaryError}
            onGenerate={onResearch}
            onOpenTemplates={onOpenTemplates}
            busy={busy === "research"}
            onRating={onRating}
            onOpenRating={onOpenRating}
          />
        ) : tab === "reply" ? (
          <ReplyComposer
            inquiry={inquiry}
            secondaryLoading={secondaryLoading}
            secondaryError={secondaryError}
            settings={settings}
            suggestions={suggestions}
            onGenerate={onGenerate}
            onSave={onSaveDraft}
            onSend={onOpenSend}
            onRecipientsSave={onRecipientsSave}
            onPreview={onPreview}
            busy={busy}
          />
        ) : (
          <CustomerContext context={context} currentInquiryId={inquiry?.id} onOpenHistory={onOpenHistory} onSaveFollowUp={onSaveFollowUp} onAddNote={onAddNote} busy={contextBusy} />
        )}
      </div>
    </section>
  );
}
